import { useCapitalGlobalProposalStep1Form } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step1-form.context';
import { BRAZIL_UF_SELECT_OPTIONS } from '@/features/proposals/components/capital-global/constants/brazil-uf-select-options.constant';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { Text } from '@noorden-insurtech/alba-design-system';
import { DateTime } from 'luxon';

const DOCUMENT_PLACEHOLDERS: Record<'cnpj' | 'cpf' | 'caepf', string> = {
  cnpj: 'XX.XXX.XXX/XXXX-XX',
  cpf: 'XXX.XXX.XXX-XX',
  caepf: 'XXX.XXX.XXX/XXX-XX',
};

const GENDER_SELECT_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
];

export function CapitalGlobalProposalStipulantCompanyRegistrationSection() {
  const {
    form,
    isViewMode,
    isLoadingRegCep,
    canEditRegStreetNeighborhood,
    zipcodeLookupCacheRef,
    fetchAndFillReg,
    shouldShowStep1FieldErrors,
    documentLabel,
    documentMask,
  } = useCapitalGlobalProposalStep1Form();

  // Limites de idade da Data de Nascimento (CAEPF): 18 a 99 anos — mesmo do Residencial.
  const todayISODate = DateTime.now().toISODate() || '';
  const minBirthDate = DateTime.now().minus({ years: 99 }).toISODate() || '';

  return (
    <div className='flex flex-col gap-6'>
      <Text className='text-base font-normal text-alba-black-500'>
        Dados do Estipulante
      </Text>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <form.AppField
          name='cnpj'
          children={field => (
            <field.TextField
              label={documentLabel}
              placeholder={DOCUMENT_PLACEHOLDERS[documentMask]}
              required
              disabled
              mask={documentMask}
            />
          )}
        />
        <form.AppField
          name='companyName'
          children={field => (
            <field.TextField
              label='Razão Social'
              placeholder='Razão Social da Empresa'
              required
              disabled
            />
          )}
        />
      </div>

      {documentMask === 'caepf' ? (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          <form.AppField
            name='gender'
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                if (!String(value || '').trim()) return 'Sexo é obrigatório';
                return undefined;
              },
            }}
            children={field => (
              <field.SelectField
                label='Sexo'
                placeholder='Selecione'
                required
                options={GENDER_SELECT_OPTIONS}
                disabled={isViewMode}
              />
            )}
          />
          <form.AppField
            name='dateOfBirth'
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                const raw = String(value || '').trim();
                if (!raw) return 'Data de nascimento é obrigatória';
                const parsed = DateTime.fromISO(raw);
                if (!parsed.isValid) return 'Data de nascimento inválida';
                const ageInYears = DateTime.now().diff(parsed, 'years').years;
                if (ageInYears < 18 || ageInYears > 99)
                  return 'A data de nascimento não é válida!';
                return undefined;
              },
            }}
            children={field => (
              <field.DateField
                label='Data de Nascimento'
                placeholder='dd/mm/aaaa'
                required
                minDate={minBirthDate}
                maxDate={todayISODate}
                disabled={isViewMode}
              />
            )}
          />
        </div>
      ) : null}

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <form.AppField
          name='registrationCep'
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
              const s = String(value || '').trim();
              if (!s) return 'CEP é obrigatório';
              const clean = s.replace(/\D/g, '');
              if (clean.length !== 8) return 'CEP deve ter 8 dígitos';
              if (zipcodeLookupCacheRef.current[clean] === false) {
                return 'CEP inválido ou não encontrado';
              }
              return undefined;
            },
          }}
          children={field => (
            <field.TextField
              label='CEP'
              placeholder='XXXXX-XXX'
              required
              maxLength={FIELD_MAX_LENGTHS.cep}
              mask='99999-999'
              disabled={isViewMode || isLoadingRegCep}
              onBlur={async () => {
                const raw = form.state.values.registrationCep;
                const clean = String(raw || '').replace(/\D/g, '');
                if (clean.length !== 8) return;
                await fetchAndFillReg(String(raw || ''));
                await form.validateField('registrationCep', 'change');
              }}
            />
          )}
        />
      </div>

      <form.AppField
        name='registrationStreet'
        validators={{
          onChange: ({ value, fieldApi }) => {
            if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
            if (!String(value || '').trim()) return 'Endereço é obrigatório';
            return undefined;
          },
        }}
        children={field => (
          <field.TextField
            label='Endereço'
            placeholder='Rua, Avenida ...'
            required
            disabled={
              isViewMode ||
              (!canEditRegStreetNeighborhood &&
                !!form.state.values.registrationStreet)
            }
          />
        )}
      />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <form.AppField
          name='registrationNumber'
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
              if (!String(value || '').trim()) return 'Número é obrigatório';
              return undefined;
            },
          }}
          children={field => (
            <field.TextField
              label='Número'
              placeholder='Número'
              required
              maxLength={FIELD_MAX_LENGTHS.addressNumber}
              disabled={isViewMode}
            />
          )}
        />
        <form.AppField
          name='registrationComplement'
          children={field => (
            <field.TextField
              label='Complemento'
              placeholder='Apto, Bloco ...'
              maxLength={FIELD_MAX_LENGTHS.complement}
              disabled={isViewMode}
            />
          )}
        />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        <form.AppField
          name='registrationNeighborhood'
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
              if (!String(value || '').trim()) return 'Bairro é obrigatório';
              return undefined;
            },
          }}
          children={field => (
            <field.TextField
              label='Bairro'
              placeholder='Bairro'
              required
              maxLength={FIELD_MAX_LENGTHS.neighborhood}
              disabled={
                isViewMode ||
                (!canEditRegStreetNeighborhood &&
                  !!form.state.values.registrationNeighborhood)
              }
            />
          )}
        />
        <form.AppField
          name='registrationCity'
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
              if (!String(value || '').trim()) return 'Cidade é obrigatória';
              return undefined;
            },
          }}
          children={field => (
            <field.TextField
              label='Cidade'
              placeholder='Cidade'
              required
              maxLength={FIELD_MAX_LENGTHS.city}
              disabled
            />
          )}
        />
        <form.AppField
          name='registrationState'
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
              if (!String(value || '').trim()) return 'UF é obrigatória';
              return undefined;
            },
          }}
          children={field => (
            <field.SelectField
              label='Estado'
              placeholder='UF'
              required
              options={BRAZIL_UF_SELECT_OPTIONS}
              disabled
            />
          )}
        />
      </div>
    </div>
  );
}
